/* eslint-disable no-extra-parens */
import React, { useEffect, useRef, useState } from 'react';
import { NoWidgetCamData } from '../sharedComponents/noWidgetCamData.jsx';
import { LazyLoadImg } from '../../../common/utilities/lazyLoadImg.js';
import { reduxConnect } from '../../../common/index.js';
import { MediaWidgetView } from '../../common/mediaWidgets/mediaWidgetView';
import { mediaWidgetImageStyle } from '../../common/mediaWidgets/mediaImageWidgetStyle';
import { translate } from '../../../common/translation/translate.js';
import { getWidgetHeaderText } from '../../../common/utilities/getWidgetHeaderUtil';
import type { IStoreState } from '../../common/interfaces/types.js';

const ROUTE = '/traffic-cam/';
const DEFAULT_TRAFFIC_WIDGET_IMAGE = '/images/trafficCamThumbnails/weatherbug_traffic_cams_widget_img9.jpg';
const IS_WEATHER_CAM_WIDGET = false;

interface TrafficCamsWidgetProps {
    pageId: string;
    busDomain: string;
    latitude: number;
    longitude: number;
    cityName: string;
    slugName: string;
    stateAbbreviation: string;
    enableTrafficCamerasPageOnWeb2: boolean;
}

const mapStoreStateToProps = (storeState: IStoreState) => {
    const appState = storeState?.appState?.appState || {};
    const { pageId, user, enableTrafficCamerasPageOnWeb2 } = appState || {};
    const { 
        SlugName: slugName,
        Latitude: latitude, 
        Longitude: longitude,
        CityName: cityName, 
        TerritoryAbbr: stateAbbreviation
    } = user?.ActiveLocation?.Locality || {};

    return {
        pageId,
        latitude,
        longitude,
        slugName,
        cityName,
        stateAbbreviation,
        enableTrafficCamerasPageOnWeb2
    };
};

const mapDispatchToProps = {};

export const TrafficCamsWidget = reduxConnect(mapStoreStateToProps, mapDispatchToProps)(({
    pageId,
    latitude,
    longitude,
    slugName,
    cityName,
    stateAbbreviation,
    enableTrafficCamerasPageOnWeb2
}: TrafficCamsWidgetProps) => {
    const [errorLoadingImage, setErrorLoadingImage] = useState(false);
    const [imagePathUrl, setImagePathUrl] = useState(DEFAULT_TRAFFIC_WIDGET_IMAGE);
    const [initialLoadComplete, setInitialLoadComplete] = useState(false);

    const prevLatitude = useRef<number | undefined>();
    const prevLongitude = useRef<number | undefined>();
    const prevPageId = useRef<string | undefined>();

    useEffect(() => {
        const tryLoadInitialData = () => {
            if (!initialLoadComplete) {
                const defaultImgPath = '/images/trafficCamThumbnails/weatherbug_traffic_cams_widget_img';
                const thumbnailNumber = Math.floor(Math.random() * 9) + 1;
                const newImagePathUrl = `${defaultImgPath}${thumbnailNumber}.jpg`;
            
                if (thumbnailNumber >= 1 && thumbnailNumber <= 9) {
                    setImagePathUrl(newImagePathUrl);
                }
                setInitialLoadComplete(true);
            }
        };
    
        if (!initialLoadComplete || (initialLoadComplete && (latitude !== prevLatitude.current || longitude !== prevLongitude.current || pageId !== prevPageId.current))) {
            setInitialLoadComplete(false);
            setErrorLoadingImage(false);
            tryLoadInitialData();
        }
      
        prevLatitude.current = latitude;
        prevLongitude.current = longitude;
        prevPageId.current = pageId;
    }, [latitude, longitude, pageId, initialLoadComplete]);
    
    const onImgError = () => {
        setErrorLoadingImage(true);
    };

    const linkURL = `${ROUTE}${slugName}`;
    const widgetTitle = translate('trafficCameras');
    const widgetHeaderText = getWidgetHeaderText({ widgetTitle, cityName, stateAbbreviation });

    return (
        <MediaWidgetView
            isErrorView={errorLoadingImage}
            routeToPage={linkURL}
            widgetHeaderText={widgetHeaderText}
            showArrowIcon={true}
            enableTrafficCamerasPageOnWeb2={enableTrafficCamerasPageOnWeb2}
            bodyChildren={
                errorLoadingImage ? (
                    <NoWidgetCamData noAvailableCam={false} isWeatherCamWidget={IS_WEATHER_CAM_WIDGET} />
                ) : (
                    <LazyLoadImg
                        style={mediaWidgetImageStyle}
                        width={mediaWidgetImageStyle.width}
                        height={mediaWidgetImageStyle.height}
                        alt='WeatherBug Traffic Cameras widget'
                        src={imagePathUrl}
                        handleImageLoadError={onImgError}
                    />
                )
            }
        />
    );

});